import { useContext } from "react";
import { useReactiveVar } from "@apollo/client";
import { branchesLength } from "@me-team/host/main/branches/state";
import { CompanyAccess, PermissionScope } from "@me-team/host/main/graphql/types";
import { AccessContext } from "@me-team/host/src/components/App/App";
import AccessService from "@me-team/host/services/AccessService";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

export const useWorkScheduleAccess = () => {
    const { isReady: isAccessContextReady } = useContext(AccessContext);
    const totalActiveBranches = useReactiveVar(branchesLength);
    const accessService = AccessService.getInstance();

    const canViewWorkScheduleAll = useRoleAccess(CompanyAccess.ScheduleCalendarView, {
        scope: PermissionScope.All,
    });
    const canViewWorkScheduleSelf = useRoleAccess(CompanyAccess.ScheduleCalendarView, {
        scope: PermissionScope.Self,
    });
    const canOpenEmployeeLinkAll = useRoleAccess(CompanyAccess.EmployeeView, {
        scope: PermissionScope.All,
    });
    const canOpenEmployeeLinkSelf = useRoleAccess(CompanyAccess.EmployeeView, {
        scope: PermissionScope.Self,
    });
    const canCreateVacationAll = useRoleAccess(CompanyAccess.VacationCreate, {
        scope: PermissionScope.All,
    });
    const canCreateVacationSelf = useRoleAccess(CompanyAccess.VacationCreate, {
        scope: PermissionScope.Self,
    });
    const canCreateCustomDayAll = useRoleAccess(CompanyAccess.CustomDayCreate, {
        scope: PermissionScope.All,
    });
    const canCreateCustomDaySelf = useRoleAccess(CompanyAccess.CustomDayCreate, {
        scope: PermissionScope.Self,
    });
    const canEditCustomDayAll = useRoleAccess(CompanyAccess.CustomDayEdit, {
        scope: PermissionScope.All,
    });
    const canEditCustomDaySelf = useRoleAccess(CompanyAccess.CustomDayEdit, {
        scope: PermissionScope.Self,
    });
    const canCreateScheduleAll = useRoleAccess(CompanyAccess.ScheduleCreate, {
        scope: PermissionScope.All,
    });
    const canCreateScheduleSelf = useRoleAccess(CompanyAccess.ScheduleCreate, {
        scope: PermissionScope.Self,
    });
    const canEditScheduleAll = useRoleAccess(CompanyAccess.ScheduleEdit, {
        scope: PermissionScope.All,
    });
    const canEditScheduleSelf = useRoleAccess(CompanyAccess.ScheduleEdit, {
        scope: PermissionScope.Self,
    });
    const canDeleteScheduleAll = useRoleAccess(CompanyAccess.ScheduleDelete, {
        scope: PermissionScope.All,
    });
    const canDeleteScheduleSelf = useRoleAccess(CompanyAccess.ScheduleDelete, {
        scope: PermissionScope.Self,
    });
    const canManageBranchEmployees = useRoleAccess(CompanyAccess.AccessManageBranchEmployees);

    const canViewWorkSchedulePage = canViewWorkScheduleAll || canViewWorkScheduleSelf;
    const canViewWorkSchedulePageShellActions = canViewWorkScheduleAll;
    const isWorkScheduleAccessPending =
        !isAccessContextReady || !accessService.isRoleAccessReady() || accessService.isRoleAccessLoading();
    const workSchedulePageAccessMode = canViewWorkSchedulePage ? "view" : "forbidden";
    const workScheduleAccessContract = {
        isWorkScheduleAccessPending,
        canViewWorkScheduleAll,
        canViewWorkScheduleSelf,
        canViewWorkSchedulePage,
        canViewWorkSchedulePageShellActions,
        workSchedulePageAccessMode,
    };
    const shouldShowOnlyOwnWorkSchedule =
        workScheduleAccessContract.canViewWorkScheduleSelf && !workScheduleAccessContract.canViewWorkScheduleAll;
    const shouldHideEmployeeFilter = shouldShowOnlyOwnWorkSchedule;
    const hasAddEmployeeFromAnotherBranchContext = totalActiveBranches > 1;
    const canShowAddEmployeeFromAnotherBranchAction =
        workScheduleAccessContract.canViewWorkSchedulePageShellActions &&
        canManageBranchEmployees &&
        hasAddEmployeeFromAnotherBranchContext;

    return {
        canOpenEmployeeLinkAll,
        canOpenEmployeeLinkSelf,
        canCreateVacationAll,
        canCreateVacationSelf,
        canCreateCustomDayAll,
        canCreateCustomDaySelf,
        canEditCustomDayAll,
        canEditCustomDaySelf,
        canCreateScheduleAll,
        canCreateScheduleSelf,
        canEditScheduleAll,
        canEditScheduleSelf,
        canDeleteScheduleAll,
        canDeleteScheduleSelf,
        canManageBranchEmployees,
        workScheduleAccessContract,
        shouldShowOnlyOwnWorkSchedule,
        shouldHideEmployeeFilter,
        canShowAddEmployeeFromAnotherBranchAction,
    };
};
